import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Handshake, Plus, ShieldAlert, Clock } from "lucide-react";
import ContractsPanel from "@/components/deals/ContractsPanel";
import CoachPanel from "@/components/deals/CoachPanel";
import RoutingPanel from "@/components/deals/RoutingPanel";
import LoadsPanel from "@/components/deals/LoadsPanel";
import ChannelsPanel from "@/components/deals/ChannelsPanel";
import VaultPanel from "@/components/deals/VaultPanel";
import EscrowPanel from "@/components/deals/EscrowPanel";
import DisputesPanel from "@/components/deals/DisputesPanel";
import PreDealPanel from "@/components/deals/PreDealPanel";
import PaymentTermsPanel from "@/components/deals/PaymentTermsPanel";

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "JPY", "CNY", "INR", "MXN", "BRL"];

const BLANK = {
  buyer_user_id: "", seller_user_id: "", buyer_representative: "", seller_representative: "",
  commodity: "", description: "", quantity: "", unit_of_measure: "", unit_price: "",
  currency: "USD", origin: "", destination: "", delivery_date: "",
};

function StatusPill({ status }) {
  return (
    <span data-testid="deal-status-pill"
      className="rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-white">
      {status?.replace(/_/g, " ")}
    </span>
  );
}

export default function DealRoom() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [deals, setDeals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [audit, setAudit] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [scopeAll, setScopeAll] = useState(false);
  const [tab, setTab] = useState("coach");
  const [deepRef, setDeepRef] = useState(null);
  const [form, setForm] = useState({ ...BLANK, buyer_user_id: user?.user_id || "" });

  const STAFF_ROLES = ["deal_manager", "admin", "super_admin", "compliance_director", "senior_verification_manager", "compliance_reviewer"];
  const isStaff = STAFF_ROLES.includes(user?.role);

  const load = useCallback(() => {
    const params = scopeAll && isStaff ? { scope: "all" } : {};
    api.get("/deals", { params }).then(({ data }) => setDeals(data.deals || [])).catch(() => {});
  }, [scopeAll, isStaff]);
  useEffect(() => { load(); }, [load]);

  const openDeal = (d) => {
    setSelected(d);
    setTab("coach");
    api.get(`/deals/${d.deal_id}/audit`).then(({ data }) => setAudit(data.audit || [])).catch(() => setAudit([]));
  };

  const refreshSelected = useCallback(() => {
    if (!selected) return;
    api.get(`/deals/${selected.deal_id}`).then(({ data }) => setSelected(data.deal || data)).catch(() => {});
    load();
  }, [selected, load]);

  // Deep-link from the notification bell: /deals?deal=<id>&tab=<tab>&ref=<record>
  useEffect(() => {
    const dealId = searchParams.get("deal");
    const wantTab = searchParams.get("tab");
    const ref = searchParams.get("ref");
    if (!dealId || deals.length === 0) return;
    const match = deals.find((d) => d.deal_id === dealId);
    if (match) {
      setSelected(match);
      api.get(`/deals/${dealId}/audit`).then(({ data }) => setAudit(data.audit || [])).catch(() => setAudit([]));
      setTab(wantTab || "coach");
      setDeepRef(ref || null);
      setSearchParams({}, { replace: true });
    } else if (isStaff && !scopeAll) {
      // Staff-queue notification for a deal they don't participate in — widen scope so it opens.
      setScopeAll(true);
    } else {
      // Deal is not visible to this user (no longer a participant / removed) — don't leave it stuck.
      toast.info("That item is no longer available to you.");
      setSearchParams({}, { replace: true });
    }
  }, [deals, searchParams, setSearchParams, isStaff, scopeAll]);

  // Scroll to + briefly highlight the exact record a notification points at.
  useEffect(() => {
    if (!deepRef || !selected) return;
    const t = setTimeout(() => {
      const seg = deepRef.split(":")[0];
      const nodes = document.querySelectorAll("[data-testid]");
      let el = null;
      for (const n of nodes) {
        const id = n.getAttribute("data-testid") || "";
        // Prefer a full-ref match; only fall back to the first segment if it's specific enough.
        if (id.includes(deepRef) || (seg.length >= 8 && id.includes(seg))) { el = n; break; }
      }
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("deeplink-focus");
        setTimeout(() => el.classList.remove("deeplink-focus"), 3400);
      } else {
        toast.info("Opened the relevant section — the specific item may no longer be available.");
      }
      setDeepRef(null);
    }, 700);
    return () => clearTimeout(t);
  }, [deepRef, selected, tab]);

  // Maps a backend-provided route registry entry to the correct in-app destination.
  const routeToTab = (route) => {
    if (!route) return;
    if (route.includes("/contracts")) setTab("contracts");
    else if (route.includes("/disputes")) setTab("disputes");
    else if (route.includes("/escrow")) setTab("escrow");
    else if (route.includes("/routing")) setTab("routing");
    else if (route.includes("/loads") || route.includes("/logistics")) setTab("logistics");
    else if (route.includes("/documents") || route.includes("/vault")) setTab("vault");
    else if (route === "/support") toast.info("Support: help@deitrademarch.example (preview)");
    else if (route === "/onboarding") window.location.assign("/onboarding");
    else if (/^\/deals\/[^/]+$/.test(route)) toast.info("Review the deal terms above, then advance the deal.");
    else toast.info(`Open: ${route}`);
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      quantity: parseFloat(form.quantity),
      unit_price: parseFloat(form.unit_price),
    };
    api.post("/deals", payload)
      .then(({ data }) => {
        toast.success("Deal draft created (SIMULATED — no live funds)");
        setShowForm(false);
        setForm({ ...BLANK, buyer_user_id: user?.user_id || "" });
        load();
        openDeal(data);
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Could not create deal"));
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AppShell title="Deal Room" subtitle="DRAFT / SIMULATED — no live funds, escrow or contracts in this phase.">
      <div data-testid="deal-room-banner"
        className="mb-6 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
        <ShieldAlert className="h-5 w-5" />
        <p className="text-sm">Deal Room is in <strong>preview/simulation</strong>. Contracts are DRAFT (legal review required); live funding and escrow remain disabled pending legal &amp; provider sign-off.</p>
      </div>

      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-heading text-lg font-bold text-[#0A0F1C]">Your deals</h2>
        <div className="flex items-center gap-2">
          {isStaff && (
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mr-1" data-testid="staff-all-deals-toggle">
              <input type="checkbox" checked={scopeAll} onChange={(e) => setScopeAll(e.target.checked)} /> All deals (staff)
            </label>
          )}
          <button data-testid="new-deal-btn" onClick={() => setShowForm((s) => !s)}
            className="inline-flex items-center gap-2 rounded-lg bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition-colors">
            <Plus size={16} /> New deal
          </button>
        </div>
      </div>

      {showForm && (
        <form data-testid="deal-form" onSubmit={submit}
          className="mb-8 grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2">
          {[
            ["seller_user_id", "Seller business (user id)"],
            ["buyer_representative", "Buyer representative"],
            ["seller_representative", "Seller representative"],
            ["commodity", "Commodity / product"],
            ["description", "Description"],
            ["quantity", "Quantity"],
            ["unit_of_measure", "Unit of measure (e.g. MT)"],
            ["unit_price", "Unit price"],
            ["origin", "Pickup / origin"],
            ["destination", "Delivery / destination"],
            ["delivery_date", "Delivery date / window"],
          ].map(([k, label]) => (
            <label key={k} className="text-sm">
              <span className="mb-1 block font-medium text-slate-600">{label}</span>
              <input data-testid={`deal-field-${k}`} value={form[k]} onChange={set(k)} required
                type={["quantity", "unit_price"].includes(k) ? "number" : "text"} step="any"
                className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none" />
            </label>
          ))}
          <label className="text-sm">
            <span className="mb-1 block font-medium text-slate-600">Currency</span>
            <select data-testid="deal-field-currency" value={form.currency} onChange={set("currency")}
              className="w-full rounded-md border border-slate-300 px-3 py-2">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <div className="sm:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setShowForm(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium">Cancel</button>
            <button type="submit" data-testid="deal-create-submit"
              className="rounded-lg bg-[#0A0F1C] px-4 py-2 text-sm font-semibold text-white">Create draft</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div data-testid="deals-list" className="space-y-3 lg:col-span-1">
          {deals.length === 0 && <p className="text-sm text-slate-500">No deals yet.</p>}
          {deals.map((d) => (
            <button key={d.deal_id} data-testid={`deal-card-${d.deal_id}`} onClick={() => openDeal(d)}
              className={`w-full rounded-xl border p-4 text-left transition-colors ${
                selected?.deal_id === d.deal_id ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 font-semibold text-[#0A0F1C]">
                  <Handshake size={16} /> {d.commodity}
                </span>
                <StatusPill status={d.status} />
              </div>
              <p className="mt-1 text-sm text-slate-500">{d.quantity} {d.unit_of_measure} · {d.currency} {d.total_value?.toLocaleString()}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div data-testid="deal-detail" className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-[#0A0F1C]">{selected.commodity}</h3>
                <StatusPill status={selected.status} />
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-slate-400">Total</dt><dd className="font-medium">{selected.currency} {selected.total_value?.toLocaleString()}</dd></div>
                <div><dt className="text-slate-400">Quantity</dt><dd className="font-medium">{selected.quantity} {selected.unit_of_measure}</dd></div>
                <div><dt className="text-slate-400">Origin</dt><dd className="font-medium">{selected.origin}</dd></div>
                <div><dt className="text-slate-400">Destination</dt><dd className="font-medium">{selected.destination}</dd></div>
                <div><dt className="text-slate-400">Delivery</dt><dd className="font-medium">{selected.delivery_date}</dd></div>
                <div><dt className="text-slate-400">Funding</dt><dd className="font-medium text-amber-700">{selected.funding_mode}</dd></div>
              </dl>
              <div className="mt-5">
                <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-600"><Clock size={14} /> Audit timeline</h4>
                <ul data-testid="deal-audit-list" className="max-h-32 space-y-1 overflow-y-auto text-xs text-slate-500">
                  {audit.map((e, i) => (
                    <li key={i}>{new Date(e.created_at).toLocaleString()} — <strong>{e.action}</strong> {e.previous_state && `(${e.previous_state}→${e.new_state})`}</li>
                  ))}
                </ul>
              </div>

              <div className="mt-5 flex flex-wrap gap-1 border-b border-slate-200" data-testid="deal-tabs">
                {[["coach", "Coach"], ["predeal", "Pre-Deal"], ["terms", "Payment & Terms"], ["contracts", "Contracts"], ["channels", "Channels"], ["vault", "Vault"],
                  ["routing", "Routing"], ["logistics", "Logistics"], ["escrow", "Escrow"], ["disputes", "Disputes"]].map(([k, label]) => (
                  <button key={k} data-testid={`deal-tab-${k}`} onClick={() => setTab(k)}
                    className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                      tab === k ? "border-[#2563EB] text-[#2563EB]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                    {label}
                  </button>
                ))}
              </div>

              {tab === "coach" && <CoachPanel deal={selected} onNavigate={routeToTab} />}
              {tab === "predeal" && <PreDealPanel deal={selected} onOpenContracts={() => setTab("contracts")} />}
              {tab === "terms" && <PaymentTermsPanel deal={selected} onSaved={refreshSelected} />}
              {tab === "contracts" && <ContractsPanel deal={selected} />}
              {tab === "channels" && <ChannelsPanel deal={selected} />}
              {tab === "vault" && <VaultPanel deal={selected} />}
              {tab === "routing" && <RoutingPanel deal={selected} />}
              {tab === "logistics" && <LoadsPanel deal={selected} />}
              {tab === "escrow" && <EscrowPanel deal={selected} />}
              {tab === "disputes" && <DisputesPanel deal={selected} />}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400">
              Select a deal to view details.
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
